import React from 'react';
import {
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    IconButton,
    MessageBar,
    MessageBarType,
    PanelType,
    PrimaryButton,
    SelectionMode,
    Spinner,
    Stack,
    TextField
} from '@fluentui/react';
import {toast} from 'react-toastify';
import {ILocation} from '../../models/Company';
import StandardPanel from '../Common/StandardPanel';
import {useGetLocationLinksQuery} from '../../store/endpoints/RecordLinksEndpoints';
import {
    useCreateLocationMutation,
    useDeleteLocationMutation,
    useGetLocationsForCompanyQuery,
    useSetCompanyDefaultLocationMutation,
    useUpdateLocationMutation
} from '../../store/Api';

/** Human-readable one-line label for a location (used in dropdowns and lists). */
export const locationLabel = (l: ILocation): string => {
    const parts = [l.name, l.address1, [l.city, l.stateTerritory, l.postalCodeZip].filter(Boolean).join(' ')]
        .filter(p => p && p.trim().length > 0);
    return parts.join(' — ') || 'Unnamed location';
};

interface ICompanyLocationsDisplayProps {
    companyId: string;
    /** The company's current default location id, to flag the default row. */
    defaultLocationId?: string;
}

const CompanyLocationsDisplay: React.FC<ICompanyLocationsDisplayProps> = (props) => {
    const locations = useGetLocationsForCompanyQuery({companyId: props.companyId}, {skip: !props.companyId});
    const [createLocation] = useCreateLocationMutation();
    const [updateLocation] = useUpdateLocationMutation();
    const [deleteLocation] = useDeleteLocationMutation();
    const [setDefault] = useSetCompanyDefaultLocationMutation();

    const [editing, setEditing] = React.useState<ILocation | undefined>();
    const [panelOpen, setPanelOpen] = React.useState(false);

    const openNew = () => { setEditing({company: props.companyId}); setPanelOpen(true); };
    const openEdit = (location: ILocation) => { setEditing({...location}); setPanelOpen(true); };

    const save = async (location: ILocation) => {
        const name = location.name || location.address1 || 'location';
        const toastId = toast.loading(`Saving ${name}…`);
        try {
            const result = location.id
                ? await updateLocation({companyId: props.companyId, location}).unwrap()
                : await createLocation({companyId: props.companyId, location}).unwrap();
            if (result.success === false) throw new Error(result.message);
            toast.update(toastId, {type: 'success', render: `${name} saved`, isLoading: false, autoClose: 3000});
            setPanelOpen(false);
            setEditing(undefined);
        } catch (e) {
            toast.update(toastId, {type: 'error', render: `Failed to save ${name}`, isLoading: false, autoClose: 4000});
        }
    };

    const remove = async (location: ILocation) => {
        const name = location.name || location.address1 || 'location';
        const toastId = toast.loading(`Removing ${name}…`);
        try {
            const result = await deleteLocation({companyId: props.companyId, locationId: location.id ?? ''}).unwrap();
            if (result.success === false) throw new Error(result.message);
            toast.update(toastId, {type: 'success', render: `${name} removed`, isLoading: false, autoClose: 3000});
        } catch {
            toast.update(toastId, {type: 'error', render: `Failed to remove ${name}`, isLoading: false, autoClose: 4000});
        }
    };

    const makeDefault = async (location: ILocation) => {
        const name = location.name || location.address1 || 'location';
        const toastId = toast.loading(`Setting ${name} as default…`);
        try {
            const result = await setDefault({companyId: props.companyId, locationId: location.id ?? ''}).unwrap();
            if (result.success === false) throw new Error(result.message);
            toast.update(toastId, {type: 'success', render: `${name} is now the default location`, isLoading: false, autoClose: 3000});
        } catch {
            toast.update(toastId, {type: 'error', render: `Failed to set default location`, isLoading: false, autoClose: 4000});
        }
    };

    const items = locations.data?.items ?? [];
    const columns: IColumn[] = [
        {
            key: 'name', name: 'Location', minWidth: 200, isResizable: true,
            onRender: (l: ILocation) => (
                <span>
                    {locationLabel(l)}
                    {(l.isDefault || l.id === props.defaultLocationId) && <strong style={{marginLeft: 8, color: '#5b7623'}}>(Default)</strong>}
                </span>
            )
        },
        {
            key: 'actions', name: '', minWidth: 120, maxWidth: 120,
            onRender: (l: ILocation) => (
                <Stack horizontal tokens={{childrenGap: 4}}>
                    <IconButton iconProps={{iconName: 'Edit'}} title='Edit' onClick={() => openEdit(l)} />
                    <IconButton
                        iconProps={{iconName: 'Favicon'}}
                        title='Set as default'
                        disabled={l.isDefault || l.id === props.defaultLocationId}
                        onClick={() => void makeDefault(l)}
                    />
                    <IconButton iconProps={{iconName: 'Delete'}} title='Remove' onClick={() => void remove(l)} />
                </Stack>
            )
        }
    ];

    return (
        <Stack tokens={{childrenGap: '0.5em'}}>
            <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
                <h3 style={{margin: 0}}>Locations</h3>
                <DefaultButton iconProps={{iconName: 'Add'}} text='Add Location' onClick={openNew} />
            </Stack>
            {locations.isLoading && <Spinner label='Loading locations…' />}
            {!locations.isLoading && items.length === 0 &&
                <MessageBar messageBarType={MessageBarType.info}>No locations yet. Add one to get started.</MessageBar>}
            {items.length > 0 &&
                <DetailsList
                    items={items}
                    columns={columns}
                    selectionMode={SelectionMode.none}
                    layoutMode={DetailsListLayoutMode.justified}
                    compact
                />}
            <LocationEditPanel
                isOpen={panelOpen}
                location={editing}
                onDismiss={() => { setPanelOpen(false); setEditing(undefined); }}
                onSave={save}
            />
        </Stack>
    );
};

interface ILocationEditPanelProps {
    isOpen: boolean;
    location?: ILocation;
    onDismiss: () => void;
    onSave: (location: ILocation) => Promise<void>;
}

const LocationEditPanel: React.FC<ILocationEditPanelProps> = (props) => {
    const [working, setWorking] = React.useState<ILocation>(props.location ?? {company: ''});
    const [saving, setSaving] = React.useState(false);
    const links = useGetLocationLinksQuery(props.location?.id ?? '', {skip: !props.location?.id});

    React.useEffect(() => { setWorking(props.location ?? {company: ''}); setSaving(false); }, [props.location]);

    const set = (field: keyof ILocation, value: string) => setWorking(w => ({...w, [field]: value}));

    const footer = () => (
        <Stack horizontal tokens={{childrenGap: 10}}>
            <PrimaryButton
                text='Save'
                iconProps={{iconName: 'Save'}}
                disabled={saving}
                onClick={async () => {
                    setSaving(true);
                    try {
                        await props.onSave(working);
                    } finally {
                        // On failure the panel stays open — re-enable Save so the user can retry.
                        setSaving(false);
                    }
                }}
            />
            <DefaultButton text='Cancel' onClick={props.onDismiss} />
        </Stack>
    );

    return (
        <StandardPanel
            type={PanelType.medium}
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            headerText={working.id ? 'Edit Location' : 'Add Location'}
            isFooterAtBottom
            onRenderFooterContent={footer}
            links={links.data}
        >
            <Stack tokens={{childrenGap: '0.5em'}}>
                <TextField label='Name' value={working.name ?? ''} onChange={(e, v) => set('name', v ?? '')} />
                <TextField label='Address 1' value={working.address1 ?? ''} onChange={(e, v) => set('address1', v ?? '')} />
                <TextField label='Address 2' value={working.address2 ?? ''} onChange={(e, v) => set('address2', v ?? '')} />
                <Stack horizontal tokens={{childrenGap: '1em'}}>
                    <TextField label='City' value={working.city ?? ''} onChange={(e, v) => set('city', v ?? '')} />
                    <TextField label='State' value={working.stateTerritory ?? ''} onChange={(e, v) => set('stateTerritory', v ?? '')} />
                    <TextField label='Postal Code' value={working.postalCodeZip ?? ''} onChange={(e, v) => set('postalCodeZip', v ?? '')} />
                </Stack>
                <TextField label='Country' value={working.country ?? ''} onChange={(e, v) => set('country', v ?? '')} />
                <TextField label='Phone' value={working.phone ?? ''} onChange={(e, v) => set('phone', v ?? '')} />
            </Stack>
        </StandardPanel>
    );
};

export default CompanyLocationsDisplay;
